import React, { useMemo, useState, useEffect } from 'react';
import {
    Modal,
    Box,
    Grid,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormHelperText,
    Autocomplete,
    CircularProgress,
    IconButton,
    Snackbar,
    Alert,
} from '@mui/material';
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import CloseIcon from '@mui/icons-material/Close';
import { useAuth } from 'auth/Auth';
import colors from "assets/theme/base/colors";
import AddressAutocompleteInput from "../../layouts/Travel/trips/stepForm/fields/AddressAutocompleteInput";


const formatSubtypeLabel = (value) =>
    String(value || '')
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (char) => char.toUpperCase());

const ASSET_SUBTYPES = {
    Accommodation: ['twin_share', 'dorm', 'private_room', 'villa'],
    Meal: ['breakfast', 'lunch', 'dinner', 'buffet', 'set_menu'],
    Meals: ['breakfast', 'lunch', 'dinner', 'buffet', 'set_menu'],
    Activity: ['guided_tour', 'ticket_entry', 'rental', 'trekking', 'paragliding', 'snorkeling', 'adventure'],
    Transport: ['bus_seat', 'private_car', 'shared_cab', 'boat', 'cruise'],
};

const NewMerchantAssetModal = ({
    isOpen,
    onClose,
    onSave,
    defaultMerchantId = '',
    lockMerchant = false,
}) => {
    const { axiosInstance } = useAuth();
    const [isSaving, setIsSaving] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        merchant_id: defaultMerchantId ? String(defaultMerchantId) : '',
        asset_type: '',
        subtype: '',
        package_component_id: '',
        capacity_model: 'Per Pax',
        capacity_per_slot: '',
        cost_price: '',
        geo_location: '',
        rating: '',
        status: 'Active',
        inventory_controlled: '',
    });

    const [merchants, setMerchants] = useState([]);
    const [tripLogistics, setTripLogistics] = useState([]);
    const [toast, setToast] = useState({ open: false, message: '', severity: 'info' });

    useEffect(() => {
        if (isOpen) {
            fetchData();
            setFormData((prev) => ({
                ...prev,
                merchant_id: defaultMerchantId ? String(defaultMerchantId) : prev.merchant_id,
            }));
        }
    }, [isOpen, defaultMerchantId]);

    const fetchData = async () => {
        try {
            const [accountsRes, logisticsRes] = await Promise.all([
                axiosInstance.get('/merchant-assets/merchant-options'),
                axiosInstance.get('/merchant-assets/trip-logistics-options'),
            ]);
            let merchantList = accountsRes.data || [];
            // Ensure locked/default merchant is always selectable even if options list is incomplete.
            if (defaultMerchantId) {
                const exists = merchantList.some((m) => String(m.id) === String(defaultMerchantId));
                if (!exists) {
                    merchantList = [
                        { id: Number(defaultMerchantId), name: `Merchant #${defaultMerchantId}` },
                        ...merchantList,
                    ];
                }
            }
            setMerchants(merchantList);
            setTripLogistics(logisticsRes.data || []);
        } catch (error) {
            console.error('Error fetching modal data:', error);
            setToast({
                open: true,
                message: 'Unable to load merchants or trip logistics',
                severity: 'error',
            });
        }
    };

    const handleInputChange = (e) => {
        const { name, value, checked, type } = e.target;
        setFormData(prev => {
            const next = {
                ...prev,
                [name]: type === 'checkbox' ? checked : value
            };
            if (name === 'asset_type') {
                next.subtype = '';
            }
            return next;
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (formData.inventory_controlled === '') {
            setToast({
                open: true,
                message: 'Please select Inventory Controlled value',
                severity: 'error',
            });
            return;
        }

        if (!formData.geo_location.trim()) {
            setToast({
                open: true,
                message: 'Please enter a Geo Location',
                severity: 'error',
            });
            return;
        }

        if (ASSET_SUBTYPES[formData.asset_type]?.length && !formData.subtype) {
            setToast({
                open: true,
                message: 'Please select a Subtype for the Asset Type',
                severity: 'error',
            });
            return;
        }

        setIsSaving(true);
        try {
            const payload = {
                name: formData.name,
                merchant_id: parseInt(formData.merchant_id),
                asset_type: formData.asset_type,
                subtype: formData.subtype || null,
                package_component_id: formData.package_component_id
                    ? parseInt(formData.package_component_id, 10)
                    : null,
                capacity_model: formData.capacity_model || null,
                capacity_per_slot: formData.capacity_per_slot ? parseInt(formData.capacity_per_slot) : null,
                cost_price: formData.cost_price !== '' ? parseFloat(formData.cost_price) : null,
                geo_location: formData.geo_location,
                rating: formData.rating ? parseFloat(formData.rating) : null,
                active: formData.status === 'Active',
                inventory_controlled: formData.inventory_controlled === 'Yes',
            };

            await onSave(payload);
            onClose();
            setFormData({
                name: '', merchant_id: defaultMerchantId ? String(defaultMerchantId) : '', asset_type: '',
                subtype: '',
                package_component_id: '',
                capacity_model: 'Per Pax',
                capacity_per_slot: '', cost_price: '',
                geo_location: '', rating: '', status: 'Active', inventory_controlled: ''
            });
        } catch (error) {
            console.error('Error saving Merchant Asset:', error);
            setToast({
                open: true,
                message: error.response?.data?.detail || 'Error saving Merchant Asset',
                severity: 'error'
            });
        } finally {
            setIsSaving(false);
        }
    };

    const assetTypes = ['Accommodation', 'Meal', 'Activity', 'Transport', 'Transfer', 'Guide', 'Rental', 'Other'];
    const subtypeOptions = ASSET_SUBTYPES[formData.asset_type] || [];
    const subtypeDisabled = !formData.asset_type || subtypeOptions.length === 0;

    const tripLogisticsOptions = useMemo(
        () =>
            (tripLogistics || []).map((item) => ({
                value: String(item.id),
                label: item.label || item.name || `Logistics #${item.id}`,
                category: item.category,
                package_title: item.package_title,
            })),
        [tripLogistics]
    );

    const selectedTripLogistics =
        tripLogisticsOptions.find(
            (opt) => String(opt.value) === String(formData.package_component_id || '')
        ) || null;

    return (
        <>
            <Modal open={isOpen} onClose={onClose}>
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: { xs: '95%', sm: '800px' },
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    borderRadius: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    maxHeight: '90vh',
                    overflow: 'hidden',
                }}>
                    <Box className="pearl-popup-header" sx={{
                        backgroundColor: colors.popupHeader.background,
                        px: 3,
                        py: 2,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        flexShrink: 0,
                    }}>
                        <MDTypography variant="h6" color="white">Add New Merchant Asset</MDTypography>
                        <IconButton onClick={onClose} size="small" sx={{ color: 'white' }} disabled={isSaving}>
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    <Box
                        component="form"
                        onSubmit={handleSubmit}
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            flex: 1,
                            minHeight: 0,
                            overflow: 'hidden',
                        }}
                    >
                        <Box sx={{
                            p: 4,
                            overflowY: 'auto',
                            flex: 1,
                            minHeight: 0,
                        }}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Asset Name"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        required
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth variant="outlined" margin="normal" required>
                                        <InputLabel>Merchant</InputLabel>
                                        <Select
                                            name="merchant_id"
                                            value={formData.merchant_id}
                                            onChange={handleInputChange}
                                            label="Merchant"

                                            disabled={lockMerchant}
                                        >
                                            {merchants.map(m => (
                                                <MenuItem key={m.id} value={String(m.id)}>{m.name}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth variant="outlined" margin="normal" required>
                                        <InputLabel>Asset Type</InputLabel>
                                        <Select
                                            name="asset_type"
                                            value={formData.asset_type}
                                            onChange={handleInputChange}
                                            label="Asset Type"

                                        >
                                            {assetTypes.map(type => (
                                                <MenuItem key={type} value={type}>{type}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <FormControl
                                        fullWidth
                                        variant="outlined"
                                        margin="normal"
                                        required={subtypeOptions.length > 0}
                                        disabled={subtypeDisabled}
                                    >
                                        <InputLabel>Subtype</InputLabel>
                                        <Select
                                            name="subtype"
                                            value={formData.subtype}
                                            onChange={handleInputChange}
                                            label="Subtype"

                                            disabled={subtypeDisabled}
                                        >
                                            <MenuItem value="">
                                                <em>{subtypeDisabled ? 'Select Asset Type first' : 'Select subtype'}</em>
                                            </MenuItem>
                                            {subtypeOptions.map((sub) => (
                                                <MenuItem key={sub} value={sub}>{formatSubtypeLabel(sub)}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Autocomplete
                                        options={tripLogisticsOptions}
                                        value={selectedTripLogistics}
                                        openOnFocus
                                        autoHighlight
                                        clearOnEscape
                                        onChange={(_event, next) => {
                                            setFormData((prev) => ({
                                                ...prev,
                                                package_component_id: next ? String(next.value) : '',
                                            }));
                                        }}
                                        getOptionLabel={(option) => option?.label || ''}
                                        isOptionEqualToValue={(option, value) =>
                                            String(option?.value) === String(value?.value)
                                        }
                                        filterOptions={(opts, state) => {
                                            const input = String(state.inputValue || '').trim().toLowerCase();
                                            if (!input) return opts;
                                            return opts.filter((opt) =>
                                                String(opt?.label || '').toLowerCase().includes(input)
                                            );
                                        }}
                                        ListboxProps={{
                                            sx: { maxHeight: 240 },
                                        }}
                                        slotProps={{
                                            popper: {
                                                sx: { zIndex: 1400 },
                                            },
                                        }}
                                        noOptionsText="No trip logistics found"
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Trip Logistics"
                                                placeholder="Search trip logistics..."
                                                variant="outlined"
                                                margin="normal"
                                                fullWidth
                                            />
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth variant="outlined" margin="normal">
                                        <InputLabel>Capacity Model</InputLabel>
                                        <Select
                                            name="capacity_model"
                                            value={formData.capacity_model}
                                            onChange={handleInputChange}
                                            label="Capacity Model"

                                        >
                                            <MenuItem value="Per Pax">Per Pax</MenuItem>
                                            <MenuItem value="Per Room">Per Room</MenuItem>
                                            <MenuItem value="Per Bed">Per Bed</MenuItem>
                                            <MenuItem value="Per Seat">Per Seat</MenuItem>
                                            <MenuItem value="Per Slot">Per Slot</MenuItem>
                                        </Select>
                                        <FormHelperText>
                                            Per Pax/Bed/Seat = 1 unit per person. Per Room = rooms (set max pax/room). Per Slot = booking slot capacity.
                                        </FormHelperText>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        type="number"
                                        label="Maximum Capacity per Unit"
                                        name="capacity_per_slot"
                                        value={formData.capacity_per_slot}
                                        onChange={handleInputChange}
                                        variant="outlined"
                                        margin="normal"
                                        inputProps={{ min: 1 }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        type="number"
                                        label="Cost Price"
                                        name="cost_price"
                                        value={formData.cost_price}
                                        onChange={handleInputChange}
                                        variant="outlined"
                                        margin="normal"
                                        inputProps={{ min: 0, step: 0.01 }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <MDBox mt={2} mb={1}>
                                        <AddressAutocompleteInput
                                            label="Geo Location"
                                            placeholder="Enter geo location"
                                            value={formData.geo_location || ''}
                                            onChange={(nextLocation) =>
                                                setFormData((prev) => ({ ...prev, geo_location: nextLocation }))
                                            }
                                            required
                                        />
                                    </MDBox>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        type="number"
                                        label="Rating"
                                        name="rating"
                                        value={formData.rating}
                                        onChange={handleInputChange}
                                        variant="outlined"
                                        margin="normal"
                                        inputProps={{ step: 0.1, min: 0, max: 5 }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth variant="outlined" margin="normal" required>
                                        <InputLabel>Status</InputLabel>
                                        <Select
                                            name="status"
                                            value={formData.status}
                                            onChange={handleInputChange}
                                            label="Status"

                                        >
                                            <MenuItem value="Active">Active</MenuItem>
                                            <MenuItem value="Inactive">Inactive</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth variant="outlined" margin="normal" required>
                                        <InputLabel>Inventory Controlled</InputLabel>
                                        <Select
                                            name="inventory_controlled"
                                            value={formData.inventory_controlled}
                                            onChange={handleInputChange}
                                            label="Inventory Controlled"

                                        >
                                            <MenuItem value="Yes">Yes</MenuItem>
                                            <MenuItem value="No">No</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>
                            </Grid>
                        </Box>

                        <MDBox
                            px={4}
                            py={2}
                            display="flex"
                            justifyContent="flex-end"
                            flexShrink={0}
                            sx={{
                                borderTop: '1px solid',
                                borderColor: 'divider',
                                bgcolor: 'background.paper',
                            }}
                        >
                            <MDButton onClick={onClose} color="secondary" sx={{ mr: 2 }} disabled={isSaving}>
                                Cancel
                            </MDButton>
                            <MDButton type="submit" variant="gradient" color="info" disabled={isSaving}>
                                {isSaving ? <CircularProgress size={20} color="inherit" /> : 'Save Asset'}
                            </MDButton>
                        </MDBox>
                    </Box>
                </Box>
            </Modal>

            <Snackbar
                open={toast.open}
                autoHideDuration={6000}
                onClose={() => setToast({ ...toast, open: false })}
            >
                <Alert severity={toast.severity} onClose={() => setToast({ ...toast, open: false })}>
                    {toast.message}
                </Alert>
            </Snackbar>
        </>
    );
};

export default NewMerchantAssetModal;
